import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import type { ICatalogRow } from '../../models/catalog';
import type { ISkincareQuestion } from '../../../questionario-skincare/models/skincare-question.interface';
import {
  isCatalogPriceValid,
  mapCatalogPriceToString,
  getCatalogWeightLabel,
  getCatalogWeightRangeValue,
  isCatalogWeightValid
} from '../../utils/catalogo-produtos.utils';
import { CATALOG_ROUTINE_OPTIONS, CATALOG_CATEGORY_OPTIONS } from '../../constants';

export type DialogTab = 'produto' | 'condicoes' | 'perguntas';

interface ConditionDef {
  key: 'acne' | 'wrinkles' | 'redness' | 'eyebag' | 'darkCircle' | 'oiliness';
  label: string;
}

@Component({
  selector: 'app-catalog-product-dialog',
  templateUrl: './catalog-product-dialog.component.html'
})
export class CatalogProductDialogComponent implements OnChanges {
  @Input() row: ICatalogRow | null = null;
  @Input() questions: ISkincareQuestion[] = [];
  @Input() isCreating = false;
  @Output() saved = new EventEmitter<ICatalogRow>();
  @Output() cancelled = new EventEmitter<void>();

  activeTab: DialogTab = 'produto';
  conditionSliders: Record<string, [number, number]> = {};
  editRow: ICatalogRow | null = null;

  readonly routineOptions = CATALOG_ROUTINE_OPTIONS;
  readonly categoryOptions = CATALOG_CATEGORY_OPTIONS;
  readonly minWeight = 0;
  readonly maxWeight = 100;

  readonly conditions: ConditionDef[] = [
    { key: 'acne', label: 'Acne' },
    { key: 'wrinkles', label: 'Rugas' },
    { key: 'redness', label: 'Vermelhidão' },
    { key: 'eyebag', label: 'Bolsas sob os olhos' },
    { key: 'darkCircle', label: 'Olheiras' },
    { key: 'oiliness', label: 'Oleosidade' }
  ];

  ngOnChanges(_changes: SimpleChanges | Record<string, unknown>): void {
    if (!this.row) {
      this.editRow = null;
      return;
    }
    this.editRow = {
      ...this.row,
      answersByQuestionId: { ...this.row.answersByQuestionId }
    };
    this.activeTab = 'produto';
    this.conditionSliders = {};
    for (const c of this.conditions) {
      this.conditionSliders[c.key] = this.parseRange(this.editRow[c.key]);
    }
  }

  private parseRange(value: string): [number, number] {
    if (!value) return [0, 0];
    const parts = value.split('-').map(Number);
    const a = isNaN(parts[0]) ? 0 : parts[0];
    const b = isNaN(parts[1]) ? 0 : parts[1];
    return [a, b];
  }

  onMinSlider(key: string, event: Event): void {
    const val = Number((event.target as HTMLInputElement).value);
    const cur = this.conditionSliders[key] ?? [0, 0];
    const newMin = Math.min(val, cur[1]);
    this.conditionSliders[key] = [newMin, cur[1]];
    this.syncToRow(key);
  }

  onMaxSlider(key: string, event: Event): void {
    const val = Number((event.target as HTMLInputElement).value);
    const cur = this.conditionSliders[key] ?? [0, 0];
    const newMax = Math.max(val, cur[0]);
    this.conditionSliders[key] = [cur[0], newMax];
    this.syncToRow(key);
  }

  clearCondition(key: string): void {
    this.conditionSliders[key] = [0, 0];
    this.syncToRow(key);
  }

  private syncToRow(key: string): void {
    if (!this.editRow) return;
    const [min, max] = this.conditionSliders[key];
    (this.editRow as unknown as Record<string, string>)[key] = min === 0 && max === 0 ? '' : `${min}-${max}`;
  }

  onWeightRangeInput(event: Event): void {
    if (!this.editRow) return;
    const val = Number((event.target as HTMLInputElement).value);
    this.editRow.weight = val === 0 ? '' : String(val);
  }

  clearWeight(): void {
    if (this.editRow) this.editRow.weight = '';
  }

  getWeightRangeValue(weight: string): number {
    return getCatalogWeightRangeValue(weight, this.minWeight, this.maxWeight);
  }

  getWeightLabel(weight: string): string {
    return getCatalogWeightLabel(weight);
  }

  isPriceValid(price: string): boolean {
    return isCatalogPriceValid(price);
  }

  isWeightValid(weight: string): boolean {
    return isCatalogWeightValid(weight, this.minWeight, this.maxWeight);
  }

  getQuestionOptionTexts(q: ISkincareQuestion): string[] {
    return (q.options ?? []).filter((o) => o.active).map((o) => o.text);
  }

  onSave(): void {
    if (!this.editRow) return;
    this.saved.emit({ ...this.editRow, price: mapCatalogPriceToString(this.editRow.price) });
  }

  onCancel(): void {
    this.cancelled.emit();
  }
}
